import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { depthSectorsQueryOptions } from '@/features/depth/api/queries'
import { week52NepseQueryOptions, week52QueryOptions } from '@/features/prices/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { searchTextParamSchema } from '@/schemas/common'
import { week52SortSchema } from '@/schemas/prices'
import { tableQuerySchema } from '@/schemas/table'

/**
 * `week52QuerySchema` (schemas/prices.ts) leaves `sort` required — correct
 * for the hook, which always needs one, but wrong for a route: a bare
 * `/market/52-week` has to resolve too. Moved here from the screen file, as
 * `stockSearchSchema` moved into `StockScreen.loader.ts`, so the loader below
 * and the route's `validateSearch` read the one schema.
 */
export const week52SearchSchema = tableQuerySchema.extend({
  sort: week52SortSchema.default('nearest-high'),
  sector: z.string().min(1).optional(),
  query: searchTextParamSchema.optional(),
})

/**
 * The one parse both the loader and `Week52Screen` read `search` through.
 *
 * `Route.useSearch()` merges the URL's raw query string into the validated
 * object — an unrecognised param (`?utm_source=...`) rides along even though
 * `week52SearchSchema` never named it. `week52QueryOptions` keys on the WHOLE
 * query object, so that extra field would otherwise make the screen's key
 * differ, structurally, from the one this loader warmed for the entry URL: a
 * second request, and a visible pending-then-loaded flash on a link nobody
 * meant to change anything.
 */
export const toWeek52Query = (search: unknown) => week52SearchSchema.parse(search)

/**
 * The `/market/52-week` loader.
 *
 * MAIN, keyed on the URL's whole query (`sort`/`sector`/`page`/`pageSize`/…)
 * — awaited for the ENTRY search only (no `loaderDeps`): a sort toggle, a
 * sector pick or a pager click re-runs no loader, and `Week52Screen` keeps
 * reading through the plain `useWeek52` (`placeholderData: keepPreviousData`,
 * unchanged) so that change dims the table in place instead of suspending
 * the route.
 *
 * SECONDARY, started first and not awaited: the sector menu's own names —
 * and NEPSE's row, which rides above page 1 — the sector names are
 * the same read `/market/depth`'s loader makes, and for the same reason
 * (`depthSectorsQueryOptions`'s own doc comment): nothing here is gated on
 * market status, so it is safe to fire unconditionally. Only on a real
 * navigation (`!preload`), not on hover: a member's pointer resting on a
 * `/market/52-week` link must not fire this on its own — see
 * docs/route-loading.md, "Secondary reads".
 */
export async function loadWeek52({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  if (!preload) {
    prefetchRouteData(queryClient, depthSectorsQueryOptions())
    // NEPSE's own row, pinned over the table: one entry of the shared indices read.
    prefetchRouteData(queryClient, week52NepseQueryOptions())
  }
  const search = toWeek52Query(location.search)
  await ensureRouteData(queryClient, week52QueryOptions(search))
}
